import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, CreditCard, TrendingUp, ArrowUpRight, ArrowDownRight, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

interface StatItem {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: React.ElementType;
}

interface RecentStudio {
  id: number;
  name: string;
  joinedDate: string;
  status: string;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [stats, setStats] = useState<StatItem[]>([]);
  const [recentStudios, setRecentStudios] = useState<RecentStudio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      const data = await response.json();
      // Transform API data to match our stat format
      if (data.stats) {
        setStats([
          { label: 'Total Studios', value: data.stats.totalStudios?.toString() || '0', change: data.stats.studiosChange || '+0%', trend: 'up', icon: Building2 },
          { label: 'Active Studios', value: data.stats.activeStudios?.toLocaleString() || '0', change: data.stats.activeStudiosChange || '+0%', trend: 'up', icon: Building2 },
          { label: 'Monthly Revenue', value: `$${data.stats.monthlyRevenue?.toLocaleString() || '0'}`, change: data.stats.revenueChange || '+0%', trend: 'up', icon: CreditCard },
          { label: 'Churn Rate', value: `${data.stats.churnRate || '0'}%`, change: data.stats.churnChange || '0%', trend: 'down', icon: TrendingUp },
        ]);
      }
      if (data.recentStudios) {
        setRecentStudios(data.recentStudios);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);
  return (
    <div className="space-y-8">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">Error Loading Data</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading live data...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Dashboard</h1>
        <p className="text-stone-500 dark:text-stone-400 mt-1">
          Overview of your platform metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center">
                <stat.icon className="w-6 h-6 text-sage" />
              </div>
              <div
                className={`flex items-center gap-1 text-sm font-medium ${
                  stat.trend === 'up' ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {stat.change}
                {stat.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
              </div>
            </div>
            <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{stat.value}</p>
            <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Studios */}
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100 mb-4">
            Recent Studios
          </h2>
          {recentStudios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-stone-100 dark:bg-stone-800 rounded-2xl flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-base font-medium text-stone-700 dark:text-stone-300 mb-1">No studios yet</h3>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 max-w-[200px]">
                Studios will appear here as they sign up for SereneTouch.
              </p>
              <button
                onClick={() => navigate('/studios')}
                className="px-4 py-2 bg-sage text-white text-sm font-medium rounded-lg hover:bg-sage/90 transition-colors"
              >
                View All Studios
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {recentStudios.map((studio) => (
                <div key={studio.id} className="flex items-center justify-between py-2 border-b border-stone-100 dark:border-stone-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-sage" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-700 dark:text-stone-300">{studio.name}</p>
                      <p className="text-sm text-stone-500">Joined {studio.joinedDate}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    studio.status === 'Active'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                      : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                  }`}>
                    {studio.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/studios')}
              className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl text-left hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
            >
              <Building2 className="w-6 h-6 text-sage mb-2" />
              <p className="font-medium text-stone-700 dark:text-stone-300">Add Studio</p>
              <p className="text-sm text-stone-500">Create new studio account</p>
            </button>
            <button
              onClick={() => navigate('/users')}
              className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl text-left hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
            >
              <Users className="w-6 h-6 text-sage mb-2" />
              <p className="font-medium text-stone-700 dark:text-stone-300">Invite User</p>
              <p className="text-sm text-stone-500">Send invitation email</p>
            </button>
            <button
              onClick={() => navigate('/subscriptions')}
              className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl text-left hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
            >
              <CreditCard className="w-6 h-6 text-sage mb-2" />
              <p className="font-medium text-stone-700 dark:text-stone-300">View Billing</p>
              <p className="text-sm text-stone-500">Manage subscriptions</p>
            </button>
            <button
              onClick={() => navigate('/analytics')}
              className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl text-left hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
            >
              <TrendingUp className="w-6 h-6 text-sage mb-2" />
              <p className="font-medium text-stone-700 dark:text-stone-300">Analytics</p>
              <p className="text-sm text-stone-500">View detailed reports</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
